// Loss Exceedance & Tower Placement chart — renders the empirical survival curve
// (placement.exceedanceCurve, {limit,prob}) with the customer's current tower and the
// p90/p95/p99 attach points overlaid. Pure/presentational; all data comes from the
// backend `placement` block. Ports the approved mockup
// (docs/mockups/MOCKUP_FoundationsBrokerPlacement_Roger_2026-06-16.html) to real data.
//
// Accessibility: Pat is color-blind — every band/marker is disambiguated by a TEXT label
// and position, never color alone (CLAUDE.md a11y rule).
import type { Placement } from './types'
import {
  DEFAULT_MARGINS,
  computeDomainMax,
  buildExceedancePath,
  scaleX,
  scaleY,
  plotWidth,
  plotHeight,
  formatMoneyShort,
  formatPct,
  xTicks,
  type Geom,
} from './chartGeometry'

// Risk Aperture dark brand palette (aqua hero curve on navy; status colors paired with
// text labels + shape cues below — never color alone, per the color-blind a11y rule).
const COLOR = {
  curve: '#00d9ff', // aqua
  current: '#ff4444', // danger
  p95: '#00ff88', // success
  p99: '#8892b0', // muted light
  grey: '#8892b0',
  ink: '#a8b2d1',
  line: 'rgba(255,255,255,0.12)',
  paper: '#0a192f', // navy — marker donut/triangle fill
  bandPrimary: 'rgba(0,217,255,0.06)',
  bandExcess: 'rgba(0,255,136,0.07)',
  bandTail: 'rgba(255,68,68,0.05)',
} as const
const MONO = 'ui-monospace, "SF Mono", "Cascadia Mono", monospace'
const SANS = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'

type LossExceedanceChartProps = {
  placement: Placement
  width?: number
  height?: number
  className?: string
}

function buildAriaLabel(p: Placement): string {
  const parts = [
    'Loss exceedance curve — the probability that annual cyber loss exceeds a given coverage limit.',
  ]
  if (p.coverageLimit != null && p.exceedanceAtLimit != null) {
    parts.push(
      `The current ${formatMoneyShort(p.coverageLimit)} tower is exceeded in ${formatPct(
        p.exceedanceAtLimit
      )} of modeled years.`
    )
  }
  if (p.limitToP95 != null) {
    parts.push(`Reaching the 95th percentile requires ${formatMoneyShort(p.limitToP95)}.`)
  }
  return parts.join(' ')
}

export function LossExceedanceChart({
  placement,
  width = 760,
  height = 430,
  className,
}: LossExceedanceChartProps) {
  const {
    exceedanceCurve,
    coverageLimit,
    exceedanceAtLimit,
    limitToP90,
    limitToP95,
    limitToP99,
  } = placement
  const ariaLabel = buildAriaLabel(placement)
  const svgStyle = { width: '100%', height: 'auto', display: 'block' } as const

  if (!exceedanceCurve || exceedanceCurve.length === 0) {
    return (
      <svg
        className={className}
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label={ariaLabel}
        style={svgStyle}
      >
        <text
          x={width / 2}
          y={height / 2}
          textAnchor="middle"
          fill={COLOR.grey}
          fontSize={13}
          fontFamily={MONO}
        >
          Insufficient data to model loss exceedance
        </text>
      </svg>
    )
  }

  const g: Geom = {
    width,
    height,
    ...DEFAULT_MARGINS,
    xMax: computeDomainMax(exceedanceCurve, coverageLimit, limitToP95, limitToP99),
  }
  const pH = plotHeight(g)
  const top = g.mT
  const baseY = g.mT + pH

  // Tower bands: primary (0..limit), recommended excess (limit..p95), residual tail (p95..xMax)
  const bands: JSX.Element[] = []
  if (coverageLimit != null && coverageLimit > 0) {
    bands.push(
      <rect
        key="bp"
        data-testid="band-primary"
        x={scaleX(g, 0)}
        y={top}
        width={scaleX(g, coverageLimit) - scaleX(g, 0)}
        height={pH}
        fill={COLOR.bandPrimary}
      />
    )
    if (limitToP95 != null && limitToP95 > coverageLimit) {
      bands.push(
        <rect
          key="be"
          data-testid="band-excess"
          x={scaleX(g, coverageLimit)}
          y={top}
          width={scaleX(g, limitToP95) - scaleX(g, coverageLimit)}
          height={pH}
          fill={COLOR.bandExcess}
        />,
        <rect
          key="bt"
          data-testid="band-tail"
          x={scaleX(g, limitToP95)}
          y={top}
          width={scaleX(g, g.xMax) - scaleX(g, limitToP95)}
          height={pH}
          fill={COLOR.bandTail}
        />
      )
    }
  }

  // Horizontal grid + percent axis
  const yAxis: JSX.Element[] = []
  for (let i = 0; i <= 5; i++) {
    const p = i / 5
    const y = scaleY(g, p)
    yAxis.push(
      <line
        key={`yl${i}`}
        x1={g.mL}
        y1={y}
        x2={width - g.mR}
        y2={y}
        stroke={COLOR.line}
        strokeWidth={p === 0 ? 1.2 : 0.8}
      />,
      <text
        key={`yt${i}`}
        x={g.mL - 9}
        y={y + 3.5}
        textAnchor="end"
        fill={COLOR.grey}
        fontSize={10.5}
        fontFamily={MONO}
      >
        {formatPct(p)}
      </text>
    )
  }

  // Dollar axis
  const xAxis = xTicks(g.xMax, 6).map((xv, i) => (
    <text
      key={`xt${i}`}
      x={scaleX(g, xv)}
      y={height - g.mB + 18}
      textAnchor="middle"
      fill={COLOR.grey}
      fontSize={10.5}
      fontFamily={MONO}
    >
      {formatMoneyShort(xv)}
    </text>
  ))

  // Vertical attach lines
  const vlines: JSX.Element[] = []
  const addVline = (limit: number | null, color: string, dash: string, op: number, key: string) => {
    if (limit == null || limit <= 0) return
    vlines.push(
      <line
        key={key}
        x1={scaleX(g, limit)}
        y1={top}
        x2={scaleX(g, limit)}
        y2={baseY}
        stroke={color}
        strokeWidth={1.2}
        opacity={op}
        strokeDasharray={dash}
      />
    )
  }
  addVline(coverageLimit, COLOR.current, '4 3', 0.7, 'vc')
  addVline(limitToP95, COLOR.p95, '4 3', 0.8, 'v95')
  addVline(limitToP99, COLOR.p99, '3 4', 0.5, 'v99')

  // Markers (shape + text label + sub). Shape is a color-blind-safe cue:
  // 'triangle' (▲) flags the high-risk current tower, 'circle' (●) the percentile points.
  // Percentile probs are the percentile complement.
  const markers: JSX.Element[] = []
  const addMarker = (
    limit: number | null,
    prob: number | null,
    color: string,
    label: string,
    above: boolean,
    shape: 'circle' | 'triangle',
    key: string
  ) => {
    if (limit == null || limit <= 0 || prob == null) return
    const cx = scaleX(g, limit)
    const cy = scaleY(g, prob)
    const ly = above ? cy - 16 : cy + 22
    const anchor = limit > 0.82 * g.xMax ? 'end' : limit < 0.09 * g.xMax ? 'start' : 'middle'
    const glyph =
      shape === 'triangle' ? (
        <polygon
          points={`${cx},${cy - 7.5} ${cx - 7},${cy + 5.5} ${cx + 7},${cy + 5.5}`}
          fill={color}
          stroke={COLOR.paper}
          strokeWidth={1.5}
        />
      ) : (
        <>
          <circle cx={cx} cy={cy} r={6.5} fill={COLOR.paper} stroke={color} strokeWidth={2} />
          <circle cx={cx} cy={cy} r={3} fill={color} />
        </>
      )
    markers.push(
      <g key={key}>
        {glyph}
        <text x={cx} y={ly} textAnchor={anchor} fill={color} fontSize={11.5} fontWeight={600} fontFamily={SANS}>
          {label}
        </text>
        <text x={cx} y={ly + 13} textAnchor={anchor} fill={COLOR.ink} fontSize={10} fontFamily={MONO}>
          {`${formatMoneyShort(limit)} · ${formatPct(prob)}`}
        </text>
      </g>
    )
  }
  addMarker(coverageLimit, exceedanceAtLimit, COLOR.current, 'Current tower', true, 'triangle', 'mc')
  addMarker(limitToP90, 0.1, COLOR.grey, '90th', true, 'circle', 'm90')
  addMarker(limitToP95, 0.05, COLOR.p95, '95th — recommended', true, 'circle', 'm95')
  addMarker(limitToP99, 0.01, COLOR.p99, '99th', false, 'circle', 'm99')

  const yMid = g.mT + pH / 2

  return (
    <svg
      className={className}
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label={ariaLabel}
      style={svgStyle}
    >
      {bands}
      {yAxis}
      {xAxis}
      <text
        x={g.mL + plotWidth(g) / 2}
        y={height - 8}
        textAnchor="middle"
        fill={COLOR.ink}
        fontSize={11}
        fontFamily={MONO}
      >
        Coverage limit / loss
      </text>
      <text
        x={14}
        y={yMid}
        textAnchor="middle"
        fill={COLOR.ink}
        fontSize={11}
        fontFamily={MONO}
        transform={`rotate(-90 14 ${yMid})`}
      >
        P(annual loss ≥ limit)
      </text>
      {vlines}
      <path
        data-testid="exceedance-curve"
        d={buildExceedancePath(exceedanceCurve, g)}
        fill="none"
        stroke={COLOR.curve}
        strokeWidth={3}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      {markers}
    </svg>
  )
}
